/* eslint-disable no-eq-null */
import { MinusOutlined, PlusOutlined } from '@ant-design/icons';
import { Col, DatePicker, Form, Row, Table } from 'antd';
import dayjs from 'dayjs';
import React, { useContext, useEffect, useState } from 'react';

import ButtonComponent from 'components/atoms/ButtonComponent';
import CheckboxComponent from 'components/atoms/CheckboxComponent';
import DatePickerComponent from 'components/atoms/DatePickerComponent';
import InputComponent from 'components/atoms/InputComponent';
import InputNumberComponent from 'components/atoms/InputNumberComponent';
import Paragraph from 'components/atoms/Paragraph';
import SelectComponent from 'components/atoms/SelectComponent';
import Title from 'components/atoms/Title';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import { Context } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';
import PrilohyTable from 'components/molecules/Dokumenty/PrilohyTable';
import FormWrapper from 'components/molecules/FormWrapper';
import InfoTooltip from 'components/molecules/InfoTooltip';
import UdajeEmise from 'components/pages/Emise/novyISIN/UdajeEmise';

import useProfily from 'hooks/useProfily';

import {
    EDruhCpSluzba,
    IDodatekAkcie,
    IDodatekDluh,
    IDodatekDluhDatum,
    IDodatekInvcert,
    IDodatekIsins,
    IDodatekNkoliv,
    IDodatekSluzbaTypes,
    dokumentDodatekDruhCpGet,
    dokumentDodatekDruhCpPut,
    dokumentDodatekIsinGet,
    dokumentDodatekIsinPut
} from 'services/dokumenty/dodatek';

import { EPodTypDokumentu } from 'enums/dokumentyEnums';
import { EDruhCp } from 'enums/isin/udaje/druhCp';
import { EFrekvenceVyplaty } from 'enums/isin/udaje/frekvenceVyplaty';
import { ETrueFalse } from 'enums/trueFalse';

import { rozsahPpnSelect } from 'data/codebooks/dokumenty/rozsahPpnSelect';
import trueFalseSelect from 'data/codebooks/trueFalseSelect';

import { convertToBooleans } from 'utils/convertStringBooleans';
import formatInputNumberThousand from 'utils/formatInputNumberThousand';
import getLanguageObject from 'utils/getLanguageObject';
import isinValidation from 'utils/validations/isinValidation';

export const frekvenceVyplatyPocet = {
    A: 1,
    S: 2,
    Q: 4,
    M: 12,
    W: 52,
    D: 365,
    B: 1,
    X: 0,
    NA: 0
};

interface IIsinSelectPage {
    nextStep: () => void;
    prevStep: () => void;
    zadostID: number;
    dodatekData?: IDodatekIsins;
    setDotatekData: React.Dispatch<React.SetStateAction<IDodatekIsins | undefined>>;
    signingDisabled: boolean;
}

const IsinSelectPage: React.FC<IIsinSelectPage> = ({
    nextStep,
    prevStep,
    zadostID,
    dodatekData,
    setDotatekData,
    signingDisabled
}) => {
    const { setLevel } = useProfily();
    const { cisMeny, dokumenty, language } = useContext(Context);
    const [formDisplayOnly] = Form.useForm();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState<boolean>(false);
    const [isChecked, setIsChecked] = useState<boolean>(false);
    const [, rerender] = useState<boolean>(false);
    const [cpData, setCpData] = useState<IDodatekAkcie | IDodatekDluh | IDodatekNkoliv | IDodatekInvcert>();
    const [paginationCurrent, setPaginationCurrent] = useState<number>(1);
    const [pickerVal, setPickerVal] = useState<dayjs.Dayjs>();
    const [dodatekDates, setDodatekDates] = useState<IDodatekDluhDatum[]>();
    const [dodatekDatesErr, setDodatekDatesErr] = useState<boolean>(false);
    const dluhopisSecondVariant =
        dodatekData?.data?.frekvencevyplaty === EFrekvenceVyplaty.X ||
        dodatekData?.data?.frekvencevyplaty === EFrekvenceVyplaty.NA ||
        !dodatekData?.data?.datumsplatnosti;
    const isDlt =
        dokumenty?.selectedZadost?.podtyp &&
        [EPodTypDokumentu.SMLOUVADLT, EPodTypDokumentu.DODATEKDLT, EPodTypDokumentu.NAVYSENIDLT].includes(
            dokumenty?.selectedZadost?.podtyp
        );

    const calculateNoOfDates = (frequency: EFrekvenceVyplaty) => {
        setDodatekDates(new Array(frekvenceVyplatyPocet[frequency]).fill({} as IDodatekDluhDatum));
    };

    useEffect(() => {
        setLoading(true);
        dokumentDodatekIsinGet(zadostID)
            .then(resp => {
                const respFormated = {
                    ...resp,
                    ...(resp.data && {
                        data: JSON.parse(resp.data as any)
                    })
                };
                resp.datregistr && setIsChecked(true);
                resp.datregistr &&
                    form.setFieldsValue({
                        datregistr: dayjs(resp.datregistr),
                        potvrzeno: true,
                        potvrzenoZmenregi: true
                    });
                setDotatekData(respFormated);
                setLevel('dokumenty', {
                    ...dokumenty,
                    selectedZadost: {
                        ...dokumenty?.selectedZadost!,
                        druhcp: dodatekData?.druhcp
                    }
                });
                if (Object.keys(EDruhCpSluzba).includes(respFormated.druhcp)) {
                    dokumentDodatekDruhCpGet(zadostID, respFormated.druhcp as keyof IDodatekSluzbaTypes)
                        .then(rezp => {
                            setCpData(rezp);
                            if (respFormated.druhcp === EDruhCp.DLUHOPIS) {
                                (rezp as IDodatekDluh).dluhdatumy.length
                                    ? setDodatekDates((rezp as IDodatekDluh).dluhdatumy)
                                    : respFormated.data
                                      ? calculateNoOfDates(respFormated.data.frekvencevyplaty)
                                      : null;
                            }
                            form.setFieldsValue(
                                respFormated.druhcp === EDruhCp.AKCIE
                                    ? {
                                          zamestakcppn:
                                              typeof (rezp as IDodatekAkcie).zamestakcppn === 'boolean'
                                                  ? String((rezp as IDodatekAkcie).zamestakcppn)
                                                  : undefined
                                      }
                                    : respFormated.druhcp === EDruhCp.DLUHOPIS
                                      ? {
                                            ...((rezp as IDodatekDluh)?.datemise && {
                                                datemise: dayjs((rezp as IDodatekDluh)?.datemise)
                                            }),
                                            ...((rezp as IDodatekDluh)?.datjist && {
                                                datjist: dayjs((rezp as IDodatekDluh)?.datjist)
                                            }),
                                            ...((rezp as IDodatekDluh)?.datexjist && {
                                                datexjist: dayjs((rezp as IDodatekDluh)?.datexjist)
                                            }),
                                            typdatjist: (rezp as IDodatekDluh)?.typdatjist,
                                            pocdnikup: (rezp as IDodatekDluh)?.pocdnikup,
                                            pocetcp: (rezp as IDodatekDluh)?.pocetcp,
                                            blokace:
                                                typeof (rezp as IDodatekDluh).blokace === 'boolean'
                                                    ? String((rezp as IDodatekDluh).blokace)
                                                    : undefined
                                        }
                                      : respFormated.druhcp === EDruhCp.NASTROJKOLINV ||
                                          respFormated.druhcp === EDruhCp.PODILLIST
                                        ? {
                                              mena: (rezp as IDodatekNkoliv)?.mena,
                                              // poczaknih: (rezp as IDodatekNkoliv).poczaknih,
                                              poczaknihupis: (rezp as IDodatekNkoliv).poczaknihupis,
                                              // maxobjem: (rezp as IDodatekNkoliv).maxobjem,
                                              //  celkhodnota: (rezp as IDodatekNkoliv).celkhodnota,
                                              navakcie: (rezp as IDodatekNkoliv).navakcie
                                          }
                                        : {
                                              poczaknihupis: (rezp as IDodatekInvcert).poczaknihupis
                                          }
                            );
                        })
                        .finally(() => setLoading(false));
                } else {
                    setLoading(false);
                }
            })
            .catch(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [zadostID]);

    return (
        <LoadingWrapper loading={loading}>
            {dodatekData && (
                <>
                    <FormWrapper outForm={formDisplayOnly} onFinish={() => {}}>
                        <Title text="ISIN_PARAMS" level={5} />
                        <InputComponent
                            disabled={true}
                            initialValue={dodatekData?.isin}
                            label="ISIN"
                            name="isin"
                            notRequired={true}
                        />

                        <InputComponent
                            disabled={true}
                            initialValue={dodatekData?.nazev}
                            label={!dodatekData?.data ? 'FISN' : 'NAZEV_EMISE'}
                            name="nazev2"
                            notRequired={true}
                        />
                        <InputComponent
                            disabled={true}
                            initialValue={dodatekData?.cfi}
                            label="CFI"
                            name="cfi"
                            notRequired={true}
                        />

                        {dodatekData.data && (
                            <>
                                <Title text="DOKUMNET_DODATEK_ISINDETAIL_TITLE" level={4} />
                                <UdajeEmise
                                    cisMeny={cisMeny}
                                    nextStep={() => {}}
                                    prevStep={() => {}}
                                    setDruhCP={() => {}}
                                    setIdentifikacePutData={() => {}}
                                    setUdajeEmise={() => {}}
                                    setRozcestnikInput={() => {}}
                                    prevStepRef={0}
                                    udajeEmise={convertToBooleans(dodatekData.data)}
                                    displayOnly={true}
                                    druhCP={dodatekData.druhcp}
                                />
                                <Title level={5} text="DOPLNUJICI_INFORMACE" dividerTitle={true} />
                                <InputComponent
                                    maxLength={19}
                                    name="kapital"
                                    label="ISIN_IDENTIFIKACE_KAPITAL"
                                    initialValue={
                                        typeof dodatekData?.kapital === 'number'
                                            ? formatInputNumberThousand(Number(dodatekData?.kapital))
                                            : undefined
                                    }
                                    disabled={true}
                                    notRequired={true}
                                />
                                <SelectComponent
                                    name="menaInfo"
                                    labelParagraph="MENA"
                                    isFormItem={true}
                                    data={cisMeny}
                                    defaultValue={dodatekData.mena}
                                    disabled={true}
                                    notRequired={true}
                                />
                                <InputComponent
                                    label="ISIN_IDENTIFIKACE_SPLACENO"
                                    name="splaceno"
                                    initialValue={dodatekData?.splaceno}
                                    maxLength={5}
                                    disabled={true}
                                    notRequired={true}
                                />
                            </>
                        )}
                    </FormWrapper>
                    <FormWrapper
                        outForm={form}
                        onFinish={values => {
                            if (dodatekData.druhcp === EDruhCp.DLUHOPIS && dodatekData?.data?.podminkysdatumem) {
                                const isErr = dodatekDates?.filter(e => !e.datexkup || !e.datrozhkup).length;
                                setDodatekDatesErr(!!isErr);
                                if (isErr) return;
                            }
                            setLoading(true);
                            const dodatekNewVals = {
                                ...dodatekData,
                                ...(values.datregistr && {
                                    datregistr: values.datregistr.format('YYYY-MM-DD')
                                }),
                                ...(values.datzaknih && {
                                    datzaknih: values.datzaknih.format('YYYY-MM-DD')
                                }),
                                ...(values.premena && {
                                    premena: values.premena === ETrueFalse.TRUE
                                }),
                                ...(values.premena === ETrueFalse.TRUE && {
                                    ...(values.denupllhu && {
                                        denupllhu: values.denupllhu.format('YYYY-MM-DD')
                                    }),
                                    ...(values.denupldodatlhu && {
                                        denupldodatlhu: values.denupldodatlhu.format('YYYY-MM-DD')
                                    }),
                                    ...(values.denuplmaju && {
                                        denuplmaju: values.denuplmaju.format('YYYY-MM-DD')
                                    })
                                }),
                                ...(dodatekData.typprevodu && {
                                    typprevodu: dodatekData.typprevodu,
                                    ...(dodatekData.zpuspredvp && {
                                        zpuspredvp: dodatekData.zpuspredvp
                                    }),
                                    ...(dodatekData.zpuspredfp && {
                                        zpuspredfp: dodatekData.zpuspredfp
                                    })
                                })
                            };
                            // datovy object pouzivam na viacerych strankach preto potrebujem
                            // zachovat hodnoty ale zmazat tie ktore sa zadavju na tejto stranke
                            // Loop through the properties in the array
                            [
                                'datregistr',
                                'datzaknih',
                                'premena',
                                'potvrzeno',
                                'denupllhu',
                                'denupldodatlhu',
                                'denuplmaju'
                            ].forEach(property => {
                                // If contains the property, do nothing
                                if (!values[property] || values[property] === null || values[property] === undefined) {
                                    // If property doesn't exist in A, delete it from B
                                    delete dodatekNewVals[property];
                                }
                            });
                            dokumentDodatekIsinPut(zadostID, dodatekNewVals)
                                .then(() => {
                                    setDotatekData(dodatekNewVals);
                                    if (Object.keys(EDruhCpSluzba).includes(dodatekData.druhcp)) {
                                        const druhCpData =
                                            dodatekData.druhcp === EDruhCp.AKCIE
                                                ? {
                                                      ...(values.zamestakcppn && {
                                                          zamestakcppn: values.zamestakcppn === ETrueFalse.TRUE
                                                      }),
                                                      ...(values.rozsahppn && {
                                                          rozsahppn: values.rozsahppn
                                                      })
                                                  }
                                                : dodatekData.druhcp === EDruhCp.DLUHOPIS
                                                  ? {
                                                        ...(values.isinjistiny && {
                                                            isinjistiny: values.isinjistiny
                                                        }),
                                                        ...(values.pocetcp != null &&
                                                            values.pocetcp !== '' && {
                                                                pocetcp: values.pocetcp
                                                            }),
                                                        ...(values.pocdnikup != null &&
                                                            values.pocdnikup !== '' && {
                                                                pocdnikup: values.pocdnikup
                                                            }),
                                                        ...(values.pocdnikup != null &&
                                                            values.pocdnijist !== '' && {
                                                                pocdnijist: values.pocdnijist
                                                            }),
                                                        ...(values.typdatjist && {
                                                            typdatjist: values.typdatjist
                                                        }),
                                                        ...(values.datemise && {
                                                            datemise: values.datemise.format('YYYY-MM-DD')
                                                        }),
                                                        ...(values.datjist && {
                                                            datjist: values.datjist.format('YYYY-MM-DD')
                                                        }),
                                                        ...(values.datexjist && {
                                                            datexjist: values.datexjist.format('YYYY-MM-DD')
                                                        }),
                                                        dluhdatumy: dodatekDates,
                                                        ...(values.blokace && {
                                                            blokace: values.blokace === ETrueFalse.TRUE
                                                        })
                                                    }
                                                  : dodatekData.druhcp === EDruhCp.NASTROJKOLINV ||
                                                      dodatekData.druhcp === EDruhCp.PODILLIST
                                                    ? {
                                                          //  poczaknih: values.poczaknih,
                                                          poczaknihupis: values.poczaknihupis,
                                                          // maxobjem: values.maxobjem,
                                                          //  celkhodnota: values.celkhodnota,
                                                          navakcie: values.navakcie,
                                                          mena: values.mena,
                                                          ...(values.datkurzu && {
                                                              datkurzu: values.datkurzu.format('YYYY-MM-DD')
                                                          })
                                                      }
                                                    : {
                                                          poczaknihupis: values.poczaknihupis
                                                      };
                                        dokumentDodatekDruhCpPut(
                                            zadostID,
                                            dodatekData.druhcp as keyof IDodatekSluzbaTypes,
                                            druhCpData
                                        )
                                            .then(() => {
                                                nextStep();
                                            })
                                            .finally(() => {
                                                setLoading(false);
                                            });
                                    } else {
                                        setLoading(false);
                                        nextStep();
                                    }
                                })
                                .catch(() => {
                                    setLoading(false);
                                });
                        }}
                    >
                        {dodatekData?.data && (
                            <>
                                <Row>
                                    <CheckboxComponent
                                        name="potvrzeno"
                                        checkboxText={
                                            <Row>
                                                <Paragraph
                                                    text="*"
                                                    style={{
                                                        color: '#ff4d4f',
                                                        fontSize: 25,
                                                        position: 'relative',
                                                        top: 4,
                                                        marginRight: 2
                                                    }}
                                                />
                                                <Title level={4} text="ISIN_OK_CHECKBOX" />
                                            </Row>
                                        }
                                        onChange={() => setIsChecked(pv => !pv)}
                                        defaultChecked={isChecked}
                                        checked={isChecked}
                                        style={{
                                            marginBottom: 4,
                                            marginTop: 10
                                        }}
                                        disabled={signingDisabled}
                                    />
                                </Row>
                                <Row>
                                    <Paragraph
                                        text="ISIN_OK_TEXT_CHANGE"
                                        style={{
                                            fontWeight: 'bold',
                                            display: 'block',
                                            position: 'relative',
                                            marginBottom: 8
                                        }}
                                    />
                                </Row>
                            </>
                        )}
                        {dodatekData.zmenregi && dodatekData.zmenosoba && (
                            <>
                                <InputComponent
                                    label="DOKUMENT_DODATEK_ZMENREGI"
                                    name="zmenosoba"
                                    initialValue={dodatekData?.zmenosoba}
                                    disabled={true}
                                    notRequired={true}
                                />
                                <CheckboxComponent
                                    name="potvrzenoZmenregi"
                                    checkboxText={
                                        <Row>
                                            <Paragraph
                                                text="*"
                                                style={{
                                                    color: '#ff4d4f',
                                                    fontSize: 25,
                                                    position: 'relative',
                                                    top: 4,
                                                    marginRight: 2
                                                }}
                                            />
                                            <Title level={4} text="PRIKAZ_ZMENREGI_DODATEK_CHKBOX" />
                                        </Row>
                                    }
                                    style={{
                                        marginBottom: 4,
                                        marginTop: 10
                                    }}
                                    disabled={signingDisabled}
                                />
                            </>
                        )}
                        {dodatekData.druhcp === EDruhCp.AKCIE ? (
                            <>
                                <SelectComponent
                                    name="zamestakcppn"
                                    labelParagraph="DOKUMENT_DODATEK_DETAIL_VYBRANECP"
                                    isFormItem={true}
                                    data={trueFalseSelect}
                                    defaultValue={
                                        typeof (cpData as IDodatekAkcie)?.zamestakcppn === 'boolean'
                                            ? String((cpData as IDodatekAkcie)?.zamestakcppn)
                                            : undefined
                                    }
                                    disabled={signingDisabled}
                                    onChange={() => rerender(pv => !pv)}
                                />
                                {form.getFieldValue('zamestakcppn') === 'true' && (
                                    <SelectComponent
                                        name="rozsahppn"
                                        labelParagraph="DOKUMENT_DODATEK_DETAIL_VYBRANECP_ANO"
                                        isFormItem={true}
                                        data={rozsahPpnSelect}
                                        defaultValue={(cpData as IDodatekAkcie)?.rozsahppn}
                                        disabled={signingDisabled}
                                    />
                                )}
                            </>
                        ) : dodatekData?.data && dodatekData.druhcp === EDruhCp.DLUHOPIS ? (
                            <>
                                <InputNumberComponent
                                    name="pocetcp"
                                    label="DODATEK_INVCERT_POCET_ZAKN"
                                    initialValue={(cpData as IDodatekDluh)?.pocetcp}
                                    disabled={signingDisabled}
                                    rules={[
                                        () => ({
                                            validator(_a: any, value: string) {
                                                if (!value) return Promise.resolve();
                                                if (
                                                    dodatekData.data?.nbti &&
                                                    Number(dodatekData.data?.nbti) < Number(value)
                                                )
                                                    return Promise.reject(
                                                        <TranslateText>DODATEK_DLUH_CP_ERR</TranslateText>
                                                    );
                                                return Promise.resolve();
                                            }
                                        })
                                    ]}
                                />

                                {dodatekData?.data?.datumsplatnosti && (
                                    <DatePickerComponent
                                        name="datumsplatnosti"
                                        initialValue={
                                            dodatekData?.data.datumsplatnosti
                                                ? dayjs(dodatekData?.data.datumsplatnosti)
                                                : undefined
                                        }
                                        disabledDate={current => current < dayjs()}
                                        disabled={true}
                                        label="DATUM_SPALTNOSTI_DLOUHOPISU"
                                        notRequired={true}
                                    />
                                )}
                                <DatePickerComponent
                                    name="datemise"
                                    initialValue={
                                        (cpData as IDodatekDluh)?.datemise
                                            ? dayjs((cpData as IDodatekDluh).datemise)
                                            : undefined
                                    }
                                    //  disabledDate={current => current < dayjs()}
                                    disabled={signingDisabled}
                                    label="DATUM_EMISE"
                                    onChange={() => rerender(pv => !pv)}
                                />
                                <Title level={4} text="DATUM_VYPLATA_KOUPONU" />
                                <InputNumberComponent
                                    name="pocdnikup"
                                    label="POCET_DNI"
                                    initialValue={(cpData as IDodatekDluh)?.pocdnikup}
                                    rules={[
                                        () => ({
                                            validator(_a: any, value: string) {
                                                if (!value) return Promise.resolve();
                                                if (Number(value) < 0)
                                                    return Promise.reject(<TranslateText>POCET_DNI_ERR</TranslateText>);
                                                return Promise.resolve();
                                            }
                                        })
                                    ]}
                                    disabled={signingDisabled}
                                    onChangeCustom={val => {
                                        if (val || val == 0) {
                                            setDodatekDates(pv =>
                                                pv?.map(e => {
                                                    if (e.datrozhkup) {
                                                        return {
                                                            datexkup: dayjs(e.datrozhkup)
                                                                .subtract(Number(val) - 1, 'days')
                                                                .format('YYYY-MM-DD'),
                                                            datrozhkup: e.datrozhkup
                                                        };
                                                    }
                                                    return e;
                                                })
                                            );
                                            const datjist = form.getFieldValue('datjist');
                                            form.getFieldValue('typdatjist') === 'KUPON' &&
                                                datjist &&
                                                form.setFieldsValue({
                                                    datexjist: dayjs(datjist).subtract(Number(val) - 1, 'days')
                                                });
                                        }
                                    }}
                                />
                                <Title text="ROZHODNE_DATUM_VYPLATY" level={5} dividerTitle={true}>
                                    {dluhopisSecondVariant && (
                                        <ButtonComponent
                                            id="c2PlusAltBtn"
                                            htmlType="button"
                                            type="primary"
                                            style={{
                                                display: 'inline-block',
                                                width: 50
                                            }}
                                            icon={dluhopisSecondVariant ? <PlusOutlined /> : undefined}
                                            onClick={() => {
                                                setDodatekDates(pv => {
                                                    return [...pv!, {} as IDodatekDluhDatum];
                                                });
                                            }}
                                            disabled={!form.getFieldValue('pocdnikup') || signingDisabled}
                                        />
                                    )}
                                </Title>
                                {!dluhopisSecondVariant && (
                                    <Paragraph
                                        text="VYPLNTE_DATUMY"
                                        style={{
                                            display: 'block',
                                            marginBottom: 10
                                        }}
                                    />
                                )}
                                <Table
                                    rowKey={(_, index) => index || 0}
                                    locale={{
                                        emptyText: <TranslateText>ZADNE_UDAJE</TranslateText>
                                    }}
                                    style={{
                                        margin: '30px 0px'
                                    }}
                                    columns={[
                                        {
                                            title: (
                                                <>
                                                    <Paragraph
                                                        text="*"
                                                        style={{
                                                            color: '#ff4d4f',
                                                            fontSize: 20,
                                                            position: 'relative',
                                                            top: 4,
                                                            marginRight: 4
                                                        }}
                                                    />
                                                    <TranslateText>ROZHODNE_DATUM_VYPLATY</TranslateText>{' '}
                                                    <InfoTooltip
                                                        title={
                                                            <>
                                                                <Row>
                                                                    <Paragraph
                                                                        text="DODATEK_DLUHOPIS_DATUM_TOOLTIP_1"
                                                                        style={{
                                                                            fontWeight: 'bold',
                                                                            color: '#fff'
                                                                        }}
                                                                    />
                                                                </Row>
                                                                <Row>
                                                                    <Paragraph
                                                                        text="DODATEK_DLUHOPIS_DATUM_TOOLTIP_2"
                                                                        style={{
                                                                            color: '#fff'
                                                                        }}
                                                                    />
                                                                </Row>
                                                                <Row>
                                                                    <Paragraph
                                                                        text="DODATEK_DLUHOPIS_DATUM_TOOLTIP_3"
                                                                        style={{
                                                                            color: '#fff'
                                                                        }}
                                                                    />
                                                                </Row>
                                                                <Row>
                                                                    <Paragraph
                                                                        text="DODATEK_DLUHOPIS_DATUM_TOOLTIP_4"
                                                                        style={{
                                                                            fontWeight: 'bold',
                                                                            color: '#fff'
                                                                        }}
                                                                    />
                                                                </Row>
                                                            </>
                                                        }
                                                    />
                                                </>
                                            ),
                                            dataIndex: 'datrozhkup',
                                            key: 'datrozhkup',
                                            render: (text: string, _row, i) => {
                                                const currentIndex = (paginationCurrent - 1) * 12 + i;

                                                return (
                                                    <DatePicker
                                                        className="TableItem"
                                                        name={`datum${currentIndex}`}
                                                        id={`datum${currentIndex}`}
                                                        value={text ? dayjs(text) : undefined}
                                                        status={dodatekDatesErr ? 'error' : undefined}
                                                        onChange={val => {
                                                            setDodatekDatesErr(false);
                                                            setDodatekDates(pv => {
                                                                return pv?.map((e, index) =>
                                                                    index === currentIndex && e
                                                                        ? ({
                                                                              datrozhkup: val
                                                                                  ? val.format('YYYY-MM-DD')
                                                                                  : undefined,

                                                                              datexkup: val
                                                                                  ? dayjs(val)
                                                                                        .subtract(
                                                                                            Number(
                                                                                                form.getFieldValue(
                                                                                                    'pocdnikup'
                                                                                                ) || 0
                                                                                            ) - 1,
                                                                                            'days'
                                                                                        )
                                                                                        .format('YYYY-MM-DD')
                                                                                  : e.datexkup
                                                                          } as IDodatekDluhDatum)
                                                                        : e
                                                                );
                                                            });
                                                        }}
                                                        pickerValue={pickerVal || dayjs(text || undefined)}
                                                        onPanelChange={val => {
                                                            setPickerVal(val);
                                                        }}
                                                        onOpenChange={val => {
                                                            !val && setPickerVal(undefined);
                                                        }}
                                                        disabled={signingDisabled}
                                                        format={['DD.MM.YYYY', 'D.M.YYYY']}
                                                        style={{
                                                            width: '100%'
                                                        }}
                                                        placeholder={getLanguageObject(language).VYBERTE_PLACEHOLDER}
                                                    />
                                                );
                                            }
                                        },
                                        {
                                            title: (
                                                <>
                                                    <Paragraph
                                                        text="*"
                                                        style={{
                                                            color: '#ff4d4f',
                                                            fontSize: 20,
                                                            position: 'relative',
                                                            top: 4,
                                                            marginRight: 4
                                                        }}
                                                    />
                                                    <TranslateText>DATUM_EX_KUPONU</TranslateText>
                                                </>
                                            ),
                                            dataIndex: 'datexkup',
                                            key: 'datexkup',
                                            render: (text: string, _row, i) => {
                                                const currentIndex = (paginationCurrent - 1) * 12 + i;
                                                return (
                                                    <DatePicker
                                                        className="TableItem"
                                                        name={`datumEx${currentIndex}`}
                                                        value={text ? dayjs(text) : undefined}
                                                        status={dodatekDatesErr ? 'error' : undefined}
                                                        onChange={val => {
                                                            setDodatekDatesErr(false);
                                                            setDodatekDates(pv => {
                                                                return pv?.map((e, index) =>
                                                                    index === currentIndex && e
                                                                        ? ({
                                                                              datrozhkup: e.datrozhkup,
                                                                              datexkup: val
                                                                                  ? val.format('YYYY-MM-DD')
                                                                                  : undefined
                                                                          } as IDodatekDluhDatum)
                                                                        : e
                                                                );
                                                            });
                                                        }}
                                                        disabled={signingDisabled}
                                                        format={['DD.MM.YYYY', 'D.M.YYYY']}
                                                        style={{
                                                            width: '100%'
                                                        }}
                                                        placeholder={getLanguageObject(language).VYBERTE_PLACEHOLDER}
                                                        pickerValue={pickerVal || dayjs(text || undefined)}
                                                        onPanelChange={val => {
                                                            setPickerVal(val);
                                                        }}
                                                        onOpenChange={val => {
                                                            !val && setPickerVal(undefined);
                                                        }}
                                                    />
                                                );
                                            }
                                        },
                                        {
                                            dataIndex: 'orignazev',
                                            key: 'orignazev',
                                            render: (_a, _b, index) => {
                                                const currentIndex = (paginationCurrent - 1) * 12 + index;
                                                return dluhopisSecondVariant ? (
                                                    <ButtonComponent
                                                        className="TableItem"
                                                        id={`c2MinusBtn${currentIndex}`}
                                                        htmlType="button"
                                                        type="default"
                                                        style={{
                                                            display: 'inline-block',
                                                            width: 50
                                                        }}
                                                        icon={<MinusOutlined />}
                                                        onClick={() => {
                                                            setTimeout(
                                                                () =>
                                                                    form.resetFields([
                                                                        `datum${currentIndex}`,
                                                                        `datumEx${currentIndex}`
                                                                    ]),
                                                                0
                                                            );
                                                            setDodatekDates(pv => {
                                                                return pv?.filter((_e, i) => i !== currentIndex);
                                                            });
                                                        }}
                                                    />
                                                ) : null;
                                            }
                                        }
                                    ]}
                                    dataSource={dodatekDates}
                                    pagination={{
                                        pageSize: 12,
                                        current: paginationCurrent,
                                        onChange: page => setPaginationCurrent(page),
                                        showSizeChanger: false
                                    }}
                                    rowClassName={(_a, i) => {
                                        const classes = [];
                                        if (i % 2) classes.push('tableRowGray');
                                        return classes.join(' ');
                                    }}
                                />
                                <div
                                    style={{
                                        marginTop: 20
                                    }}
                                />
                                {(dodatekData.druhcp === EDruhCp.DLUHOPIS
                                    ? dodatekData?.data?.podminkysdatumem
                                    : true) && (
                                    <>
                                        <SelectComponent
                                            name="typdatjist"
                                            labelParagraph="ROZHODNE_DATUM_VYPLATA_PREDCHAZIO"
                                            isFormItem={true}
                                            data={[
                                                {
                                                    value: 'KUPON',
                                                    labelcz: 'DODATEK_DLUH_VYPLATA_TYP_KUPON'
                                                },
                                                {
                                                    value: 'JINE',
                                                    labelcz: 'DODATEK_DLUH_VYPLATA_TYP_JINE'
                                                }
                                            ]}
                                            defaultValue={(cpData as IDodatekDluh)?.typdatjist}
                                            disabled={signingDisabled}
                                            onChange={val => {
                                                const datjist = form.getFieldValue('datjist');
                                                const pocetdni = form.getFieldValue(
                                                    val === 'JINE' ? 'pocdnijist' : 'pocdnikup'
                                                );

                                                datjist &&
                                                    (pocetdni || pocetdni == 0) &&
                                                    form.setFieldsValue({
                                                        datexjist: dayjs(datjist).subtract(Number(pocetdni) - 1, 'days')
                                                    });

                                                rerender(pv => !pv);
                                            }}
                                        />

                                        {form.getFieldValue('typdatjist') === 'JINE' && (
                                            <InputNumberComponent
                                                name="pocdnijist"
                                                label="POCET_DNI"
                                                initialValue={(cpData as IDodatekDluh)?.pocdnijist}
                                                disabled={signingDisabled}
                                                onChangeCustom={val => {
                                                    const datjist = form.getFieldValue('datjist');
                                                    datjist &&
                                                        val &&
                                                        form.setFieldsValue({
                                                            datexjist: dayjs(datjist).subtract(Number(val) - 1, 'days')
                                                        });
                                                }}
                                            />
                                        )}
                                        <Row justify="space-between">
                                            <Col span={10}>
                                                <DatePickerComponent
                                                    name="datjist"
                                                    initialValue={
                                                        (cpData as IDodatekDluh)?.datjist
                                                            ? dayjs((cpData as IDodatekDluh).datjist)
                                                            : undefined
                                                    }
                                                    disabledDate={current => current < dayjs()}
                                                    label="ROZHODNE_DATUM_JISTANA"
                                                    infoTooltipText={
                                                        <>
                                                            <Row>
                                                                <Paragraph
                                                                    text="DODATEK_DLUHOPIS_DATUM_TOOLTIP_1"
                                                                    style={{
                                                                        fontWeight: 'bold',
                                                                        color: '#fff'
                                                                    }}
                                                                />
                                                            </Row>
                                                            <Row>
                                                                <Paragraph
                                                                    text="DODATEK_DLUHOPIS_DATUM_TOOLTIP_2"
                                                                    style={{
                                                                        color: '#fff'
                                                                    }}
                                                                />
                                                            </Row>
                                                            <Row>
                                                                <Paragraph
                                                                    text="DODATEK_DLUHOPIS_DATUM_TOOLTIP_3"
                                                                    style={{
                                                                        color: '#fff'
                                                                    }}
                                                                />
                                                            </Row>
                                                            <Row>
                                                                <Paragraph
                                                                    text="DODATEK_DLUHOPIS_DATUM_TOOLTIP_4"
                                                                    style={{
                                                                        fontWeight: 'bold',
                                                                        color: '#fff'
                                                                    }}
                                                                />
                                                            </Row>
                                                        </>
                                                    }
                                                    disabled={
                                                        signingDisabled /* || form.getFieldValue('typdatjist') === 'KUPON' */
                                                    }
                                                    onChange={val => {
                                                        const typdatjist = form.getFieldValue('typdatjist');
                                                        const pocetdni = form.getFieldValue(
                                                            typdatjist === 'JINE' ? 'pocdnijist' : 'pocdnikup'
                                                        );
                                                        typdatjist &&
                                                            (pocetdni || pocetdni == 0) &&
                                                            val &&
                                                            form.setFieldsValue({
                                                                datexjist: val.subtract(Number(pocetdni) - 1, 'days')
                                                            });
                                                    }}
                                                />
                                            </Col>
                                            <Col span={10}>
                                                <DatePickerComponent
                                                    name="datexjist"
                                                    initialValue={
                                                        (cpData as IDodatekDluh)?.datexjist
                                                            ? dayjs((cpData as IDodatekDluh).datexjist)
                                                            : undefined
                                                    }
                                                    disabledDate={current => current < dayjs()}
                                                    disabled={
                                                        signingDisabled /* || form.getFieldValue('typdatjist') === 'KUPON' */
                                                    }
                                                    label="DATUM_EX_JISTANA"
                                                />
                                            </Col>
                                        </Row>
                                        <SelectComponent
                                            name="blokace"
                                            labelParagraph="DOKUMENT_DODATEK_DETAIL_BLOKACE"
                                            isFormItem={true}
                                            data={trueFalseSelect}
                                            defaultValue={
                                                typeof (cpData as IDodatekDluh)?.blokace === 'boolean'
                                                    ? String((cpData as IDodatekDluh)?.blokace)
                                                    : undefined
                                            }
                                            disabled={signingDisabled}
                                        />
                                    </>
                                )}
                                <Paragraph
                                    text="DOKUMENT_DODATEK_DETAIL_BLOKACE_TEXT"
                                    style={{
                                        display: 'block',
                                        position: 'relative',
                                        marginBottom: 8
                                    }}
                                />
                                <InputComponent
                                    label="ISIN_ODDELENE_JISTINY"
                                    name="isinjistiny"
                                    type="text"
                                    maxLength={12}
                                    rules={isinValidation()}
                                    initialValue={(cpData as IDodatekDluh)?.isinjistiny}
                                    capitalize={true}
                                    notRequired={true}
                                    disabled={signingDisabled}
                                />
                            </>
                        ) : dodatekData.druhcp === EDruhCp.NASTROJKOLINV || dodatekData.druhcp === EDruhCp.PODILLIST ? (
                            <>
                                <InputNumberComponent
                                    name="poczaknihupis"
                                    label={'DODATEK_INVCERT_POCET_ZAKN'}
                                    initialValue={(cpData as IDodatekNkoliv)?.poczaknihupis}
                                    disabled={signingDisabled}

                                    /* rules={[
                                        () => ({
                                            validator(_a: any, value: string) {
                                                if (!value) return Promise.resolve();
                                                if (
                                                    form.getFieldValue('poczaknih') &&
                                                    Number(form.getFieldValue('poczaknih')) < Number(value)
                                                )
                                                    return Promise.reject(
                                                        <TranslateText>DODATEK_DLUH_CP_ERR</TranslateText>
                                                    );
                                                return Promise.resolve();
                                            }
                                        })
                                    ]} */
                                />
                                {/* <InputNumberComponent
                                    name="poczaknih"
                                    label="DODATEK_INVCERT_POCET_EMISI"
                                    initialValue={(cpData as IDodatekNkoliv)?.poczaknih}
                                    disabled={signingDisabled}
                                      rules={[
                                        () => ({
                                            validator(_a: any, value: string) {
                                                if (!value) return Promise.resolve();
                                                if (
                                                    form.getFieldValue('poczaknihupis') &&
                                                    Number(form.getFieldValue('poczaknihupis')) > Number(value)
                                                )
                                                    return Promise.reject(
                                                        <TranslateText>DODATEK_DLUH_CP_ERR</TranslateText>
                                                    );
                                                return Promise.resolve();
                                            }
                                        })
                                    ]}
                                />*/}
                                <InputNumberComponent
                                    name="navakcie"
                                    label={
                                        dodatekData.druhcp === EDruhCp.NASTROJKOLINV
                                            ? 'DODATEK_INVCERT_NAV'
                                            : 'DODATEK_PODILLIST_NAV'
                                    }
                                    initialValue={(cpData as IDodatekNkoliv)?.navakcie}
                                    disabled={signingDisabled}
                                    notRequired={
                                        !!(
                                            dodatekData.druhcp === EDruhCp.PODILLIST &&
                                            dodatekData.data.mtno &&
                                            dodatekData.data.mtno > 0
                                        )
                                    }
                                    precision={4}
                                />
                                <SelectComponent
                                    name="mena"
                                    labelParagraph="DODATEK_INVCERT_NAV_MENA"
                                    isFormItem={true}
                                    data={cisMeny}
                                    onChange={() => rerender(pv => !pv)}
                                    defaultValue={(cpData as IDodatekNkoliv)?.mena}
                                    disabled={signingDisabled}
                                    allowClear={true}
                                    notRequired={
                                        !!(
                                            dodatekData.druhcp === EDruhCp.PODILLIST &&
                                            dodatekData.data.mtno &&
                                            dodatekData.data.mtno > 0
                                        )
                                    }
                                />
                                {/*  {form.getFieldValue('mena') && form.getFieldValue('mena') !== 'CZK' && (
                                    <DatePickerComponent
                                        name="datkurzu"
                                        initialValue={
                                            (cpData as IDodatekNkoliv)?.datkurzu
                                                ? dayjs((cpData as IDodatekNkoliv).datkurzu)
                                                : undefined
                                        }
                                        label="DODATEK_INVCERT_NAV_DATUM"
                                        disabled={signingDisabled}
                                        notRequired={
                                            !!(
                                                dodatekData.druhcp === EDruhCp.PODILLIST &&
                                                dodatekData.data.mtno &&
                                                dodatekData.data.mtno > 0
                                            )
                                        }
                                    />
                                )}

                                <InputNumberComponent
                                    name="celkhodnota"
                                    label="ISIN_EMISE_AKCIE_CELKOVA_HODNOTA"
                                    initialValue={(cpData as IDodatekNkoliv)?.celkhodnota}
                                    disabled={signingDisabled}
                                />
                                <InputNumberComponent
                                    name="maxobjem"
                                    label="DODATEK_INVCERT_MAX_EMISE"
                                    initialValue={(cpData as IDodatekNkoliv)?.maxobjem}
                                    disabled={signingDisabled}
                                />*/}
                            </>
                        ) : dodatekData.druhcp === EDruhCp.INVCERT ? (
                            <InputNumberComponent
                                name="poczaknihupis"
                                label="DODATEK_INVCERT_POCET_ZAKN"
                                initialValue={(cpData as IDodatekNkoliv)?.poczaknihupis}
                                disabled={signingDisabled}
                                /* rules={[
                                        () => ({
                                            validator(_a: any, value: string) {
                                                if (!value) return Promise.resolve();
                                                if (
                                                    form.getFieldValue('poczaknih') &&
                                                    Number(form.getFieldValue('poczaknih')) < Number(value)
                                                )
                                                    return Promise.reject(
                                                        <TranslateText>DODATEK_DLUH_CP_ERR</TranslateText>
                                                    );
                                                return Promise.resolve();
                                            }
                                        })
                                    ]} */
                            />
                        ) : (
                            <></>
                        )}
                        <Row>
                            <Paragraph
                                text="*"
                                style={{
                                    color: '#ff4d4f',
                                    fontSize: 23,
                                    position: 'relative',
                                    top: 4,
                                    marginRight: 2
                                }}
                            />
                            <Title level={5} text="DOKUMENT_DODATEK_DETAIL_DATUM_REHGISTRACE" />
                        </Row>
                        <Paragraph
                            text="DOKUMENT_DODATEK_DETAIL_DATUM_REHGISTRACE_TEXT"
                            style={{
                                display: 'block',
                                marginBottom: 10
                            }}
                        />
                        <DatePickerComponent
                            name="datregistr"
                            initialValue={dodatekData?.datregistr ? dayjs(dodatekData.datregistr) : undefined}
                            disabledDate={current => current < dayjs()}
                            disabled={signingDisabled}
                            /* rules={[
                                {
                                    required: true,
                                    message: <TranslateText>NEVALIDNI_POVINNA_POLOZKA</TranslateText>
                                },
                                {
                                    validator(_, value) {
                                        console.log(value);
                                        if (dayjs(value) < dayjs())
                                            return Promise.reject(
                                                <TranslateText>
                                                    DOKUMENT_DODATEK_DETAIL_DATUM_REHGISTRACE_ERR
                                                </TranslateText>
                                            );
                                        return Promise.resolve();
                                    }
                                }
                            ]} */
                        />
                        <DatePickerComponent
                            name="datzaknih"
                            label={'DOKUMENT_DODATEK_DETAIL_POZADOVANE_DATUM'}
                            initialValue={dodatekData?.datzaknih ? dayjs(dodatekData.datzaknih) : undefined}
                            disabledDate={current => current < dayjs()}
                            disabled={signingDisabled}
                            /* rules={[
                                {
                                    required: true,
                                    message: <TranslateText>NEVALIDNI_POVINNA_POLOZKA</TranslateText>
                                },
                                {
                                    validator(_, value) {
                                        console.log(form.getFieldValue('datemise'), value, dayjs(value));
                                        if (
                                            value &&
                                            form.getFieldValue('datemise') &&
                                            dayjs(value).format('YYYY-DD-MM') <
                                                form.getFieldValue('datemise').format('YYYY-DD-MM')
                                        )
                                            return Promise.reject(
                                                <TranslateText>
                                                    DOKUMENT_DODATEK_DETAIL_POZADOVANE_DATUM_ERR
                                                </TranslateText>
                                            );
                                        return Promise.resolve();
                                    }
                                }
                            ]} */
                        />
                        {(dodatekData.druhcp === EDruhCp.AKCIE || !dodatekData?.data) && (
                            <SelectComponent
                                name="premena"
                                labelParagraph="DOKUMENT_DODATEK_DETAIL_ZAKN_VYDANI"
                                isFormItem={true}
                                data={trueFalseSelect}
                                onChange={() => rerender(pv => !pv)}
                                defaultValue={
                                    typeof dodatekData?.premena === 'boolean'
                                        ? String(dodatekData.premena)
                                        : dodatekData?.premena
                                }
                                disabled={signingDisabled}
                            />
                        )}
                        {form.getFieldValue('premena') === 'true' && (
                            <>
                                <DatePickerComponent
                                    name="denupllhu"
                                    label={'DOKUMENT_DODATEK_DETAIL_LHUTA'}
                                    initialValue={dodatekData?.denupllhu ? dayjs(dodatekData.denupllhu) : undefined}
                                    // disabledDate={current => current < dayjs()}
                                    disabled={signingDisabled}
                                    notRequired={true}
                                />
                                <DatePickerComponent
                                    name="denupldodatlhu"
                                    label={'DOKUMENT_DODATEK_DETAIL_LHUTA2'}
                                    initialValue={
                                        dodatekData?.denupldodatlhu ? dayjs(dodatekData.denupldodatlhu) : undefined
                                    }
                                    //  disabledDate={current => current < dayjs()}
                                    disabled={signingDisabled}
                                    notRequired={true}
                                />
                                <DatePickerComponent
                                    name="denuplmaju"
                                    label={'DOKUMENT_DODATEK_DETAIL_LHUTA3'}
                                    initialValue={dodatekData?.denuplmaju ? dayjs(dodatekData.denuplmaju) : undefined}
                                    // disabledDate={current => current < dayjs()}
                                    disabled={signingDisabled}
                                    notRequired={true}
                                />
                            </>
                        )}
                        <Title level={5} text="DOKUMENT_DODATEK_DETAIL_ZAKN_NABYVATELE" />
                        <Paragraph
                            text={
                                isDlt
                                    ? 'DOKUMENT_DODATEK_DETAIL_ZAKN_NABYVATELE_TEXT_DLT'
                                    : 'DOKUMENT_DODATEK_DETAIL_ZAKN_NABYVATELE_TEXT'
                            }
                            style={{
                                display: 'block',
                                marginBottom: 10
                            }}
                        />
                        <PrilohyTable zadostID={zadostID} required={true} outForm={form} />
                        <Row className="button-row">
                            <ButtonComponent
                                buttontext="ZPET"
                                onClick={() => prevStep()}
                                id="isinZpetBtn"
                                type="default"
                            />
                            <ButtonComponent
                                buttontext="POKRACOVAT"
                                htmlType="submit"
                                id="isinPokracovatBtna"
                                // disabled={dluhopisDatesErr}
                                extramessage={
                                    /* dluhopisDatesErr
                                        ? 'DODATEK_DLUHOPIS_DATUM_ERR'
                                        : */ dodatekDatesErr ? 'DODATEK_DLUHOPIS_DATUM_ERR2' : ''
                                }
                            />
                        </Row>
                    </FormWrapper>
                </>
            )}
        </LoadingWrapper>
    );
};

export default IsinSelectPage;
